import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call } from "../api/client";
import { keys } from "../api/queries";
import type { AllowedAppView, ConnectedAppView } from "../api/types";
import { ConfirmDialog } from "../components/Dialog";
import { dateTime } from "../text";

/** Profile section: the applications the own account agreed to, with a way to disconnect each. */
export function ConnectedAppsSection() {
  const client = useQueryClient();
  const apps = useQuery({ queryKey: keys.connectedApps, queryFn: () => call<ConnectedAppView[]>(api.GET("/api/connected-apps")) });
  const [revoking, setRevoking] = useState<ConnectedAppView | null>(null);

  return (
    <section className="card stack" aria-labelledby="verbundene-anwendungen">
      <h3 id="verbundene-anwendungen">Verbundene Anwendungen</h3>
      {apps.isLoading && <p className="muted">Lade …</p>}
      {apps.data?.length === 0 && <p className="muted">Keine Anwendung ist mit deinem Konto verbunden.</p>}
      {apps.data && apps.data.length > 0 && (
        <ul className="user-list">
          {apps.data.map((a) => (
            <li key={a.applicationId} className="row" aria-label={a.name}>
              <span>
                <strong>{a.name}</strong> <span className="muted">{a.clientHost}</span>
                <br />
                <span className="small">{a.scopes.map((s) => s.label).join(" · ")}</span>
                <br />
                <span className="muted small">seit {dateTime(a.since)}</span>
              </span>
              <button type="button" className="link danger" onClick={() => setRevoking(a)}>
                Trennen
              </button>
            </li>
          ))}
        </ul>
      )}
      {revoking && (
        <ConfirmDialog
          title="Verbindung trennen"
          action="Trennen"
          danger
          onClose={() => setRevoking(null)}
          onConfirm={async () => {
            await call(api.DELETE("/api/connected-apps/{applicationId}", { params: { path: { applicationId: revoking.applicationId } } }));
            await client.invalidateQueries({ queryKey: keys.connectedApps });
          }}
        >
          <p>{revoking.name} verliert den Zugriff auf dein Konto. Für eine neue Verbindung musst du erneut zustimmen.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/** Admin page: the client ids of applications that may connect at all. */
export function AllowedAppsPage() {
  const client = useQueryClient();
  const apps = useQuery({ queryKey: keys.allowedApps, queryFn: () => call<AllowedAppView[]>(api.GET("/api/allowed-apps")) });
  const [clientId, setClientId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AllowedAppView | null>(null);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await call(api.POST("/api/allowed-apps", { body: { clientId } }));
      setClientId("");
      await client.invalidateQueries({ queryKey: keys.allowedApps });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="stack">
      <p className="muted small">
        Nur diese Anwendungen dürfen sich mit Konten verbinden, etwa Claude über MCP. Eine Anwendung meldet sich mit der Adresse ihres
        Metadaten-Dokuments (Client-ID). Jedes Konto muss der Verbindung danach selbst zustimmen.
      </p>
      {apps.isLoading && <p className="muted">Lade …</p>}
      {apps.data?.length === 0 && <p className="muted">Keine Anwendung ist zugelassen.</p>}
      <ul className="user-list">
        {apps.data?.map((a) => (
          <li key={a.id} className="card row" aria-label={a.name ?? a.clientId}>
            <span>
              <strong>{a.name ?? a.host}</strong>
              <br />
              <span className="muted small allowed-app-id">{a.clientId}</span>
            </span>
            <button type="button" className="link danger" onClick={() => setRemoving(a)}>
              Entfernen
            </button>
          </li>
        ))}
      </ul>
      <form className="card stack" onSubmit={(e) => void add(e)}>
        <h3>Anwendung zulassen</h3>
        <label className="field">
          <span>Client-ID (HTTPS-Adresse)</span>
          <input type="url" inputMode="url" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="https://…" required />
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="button primary">
          Zulassen
        </button>
      </form>
      {removing && (
        <ConfirmDialog
          title="Anwendung entfernen"
          action="Entfernen"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await call(api.DELETE("/api/allowed-apps/{id}", { params: { path: { id: removing.id } } }));
            await client.invalidateQueries({ queryKey: keys.allowedApps });
          }}
        >
          <p>{removing.name ?? removing.clientId} kann sich danach nicht mehr verbinden. Bestehende Verbindungen aller Konten werden getrennt.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
